/*
 * <feature-rotator> — rotating product feature highlights.
 *
 * Shows a fixed window of `visible` rows. When there are more rows than fit,
 * a new row eases in at the top while the bottom row eases out (a downward
 * ticker). Layout height is animated with the grid-template-rows 0fr/1fr
 * technique so rows of any height slide cleanly, with an opacity/translate
 * fade on the row content.
 *
 * Config comes from data attributes set by snippets/feature-highlights.liquid:
 *   data-rotate, data-visible, data-interval (ms), data-speed (ms),
 *   data-style (slide|fade), data-pause-hover (true|false)
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('feature-rotator')) {
    return;
  }

  var REDUCE =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class FeatureRotator extends HTMLElement {
    connectedCallback() {
      if (this._inited) {
        this._start();
        return;
      }
      this._inited = true;

      this.list = this.querySelector('.fh__list');
      if (!this.list) return;

      this.rows = Array.prototype.slice.call(this.list.querySelectorAll('.fh__row'));
      this.rows.forEach(function (row) {
        row.removeAttribute('hidden');
      });

      this.visible = Math.max(1, parseInt(this.dataset.visible, 10) || 3);
      this.speed = parseInt(this.dataset.speed, 10) || 500;
      this.interval = Math.max(1500, parseInt(this.dataset.interval, 10) || 4000);
      this.pauseHover = this.dataset.pauseHover === 'true';

      var canRotate =
        this.dataset.rotate === 'true' && this.rows.length > this.visible;

      // Reduced motion, or rotation off: show only the first `visible` rows
      // (no animation, no timer). With no extra rows, leave everything as-is.
      if (!canRotate || REDUCE) {
        if (this.dataset.rotate === 'true' && this.rows.length > this.visible) {
          this._detachAll();
          for (var i = 0; i < this.visible; i++) {
            this.list.appendChild(this.rows[i]);
          }
        }
        return;
      }

      this.classList.add('fh--rotating');

      // Lock the widget to a fixed height (tallest row x visible count) so the
      // internal slide never changes the outer box or pushes the page around.
      // Measured while every row is still displayed, before detaching.
      var rowHeight = 0;
      this.rows.forEach(function (row) {
        rowHeight = Math.max(rowHeight, row.offsetHeight);
      });
      if (rowHeight > 0) {
        this.list.style.height = this.visible * rowHeight + 'px';
      }

      this.head = 0;
      this._detachAll();
      var self = this;
      this._window().forEach(function (row) {
        self.list.appendChild(row);
      });
      this._start();

      if (this.pauseHover) {
        this.addEventListener('mouseenter', function () { self._stop(); });
        this.addEventListener('mouseleave', function () { self._start(); });
        this.addEventListener('focusin', function () { self._stop(); });
        this.addEventListener('focusout', function () { self._start(); });
      }
      this._onVisibility = function () {
        if (document.hidden) self._stop();
        else self._start();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    disconnectedCallback() {
      this._stop();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
    }

    _detachAll() {
      this.rows.forEach(function (row) {
        if (row.parentNode) row.parentNode.removeChild(row);
      });
    }

    _window() {
      var out = [];
      var n = this.rows.length;
      for (var i = 0; i < this.visible; i++) {
        out.push(this.rows[(this.head + i) % n]);
      }
      return out;
    }

    _start() {
      if (this.timer || !this.classList.contains('fh--rotating')) return;
      var self = this;
      this.timer = window.setInterval(function () {
        self._advance();
      }, this.interval);
    }

    _stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    _advance() {
      if (this._busy) return;
      this._busy = true;

      var self = this;
      var n = this.rows.length;
      var newHead = (this.head - 1 + n) % n;
      var incoming = this.rows[newHead];
      var outgoing = this.rows[(this.head + this.visible - 1) % n];

      // Insert the incoming row at the top in a collapsed state.
      incoming.classList.add('fh__row--enter');
      this.list.insertBefore(incoming, this.list.firstChild);
      // Force reflow so the collapsed start state commits before expanding.
      void incoming.offsetWidth;
      incoming.classList.remove('fh__row--enter');
      // Collapse the outgoing bottom row.
      outgoing.classList.add('fh__row--leave');

      window.setTimeout(function () {
        if (outgoing.parentNode === self.list) {
          self.list.removeChild(outgoing);
        }
        outgoing.classList.remove('fh__row--leave');
        self.head = newHead;
        self._busy = false;
      }, this.speed + 40);
    }
  }

  customElements.define('feature-rotator', FeatureRotator);
})();
